import DOMPurify from 'dompurify'
import {
  BENEFIT_HTML_ALLOWED_TAGS,
  BENEFIT_HTML_ALLOWED_ATTRS,
} from '../../shared/benefitHtml'

/**
 * Render-side sanitizer for the VSP "sections" benefit HTML, built on
 * DOMPurify (cure53-maintained, uses the browser's own DOM). The stored HTML
 * was already sanitized at ingest by sanitize-html
 * (functions/src/eligibilityCheck/benefitHtml.ts) with the same shared
 * policy — this pass exists so `dangerouslySetInnerHTML` never trusts
 * Firestore data, whatever wrote it.
 *
 * ALLOW_DATA_ATTR / ALLOW_ARIA_ATTR default to true in DOMPurify — disabled
 * here so the attribute policy is exactly the shared allowlist. FORBID_CONTENTS
 * is deliberately NOT overridden: cure53's default content-dropping list
 * carries mXSS protections we must not weaken.
 */
export function sanitizeBenefitHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: BENEFIT_HTML_ALLOWED_TAGS,
    ALLOWED_ATTR: BENEFIT_HTML_ALLOWED_ATTRS,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  })
}

/**
 * Mark table rows that carry no visible text — the portal's decorative
 * spacer rows (a lone `&nbsp;` cell between benefit blocks) — with our own
 * `vr-blank-row` class, so the stylesheet can render them as slim clean
 * separators. Marked rather than removed: the spacers carry the portal's
 * intended visual grouping. Detection is structural (text content only),
 * never based on the portal's class names; a row whose nested content has
 * real text is untouched.
 *
 * Also marks each list line's leading bullet-glyph element (text is just
 * "•") with `vr-bullet`, so the stylesheet can accent-color it — content
 * text is indistinguishable from a glyph by position alone (copay lists
 * have no glyph element), hence the content check.
 *
 * Runs BEFORE sanitizeBenefitHtml — `class` is allowlisted, so the markers
 * survive, and the sanitize-last order means `dangerouslySetInnerHTML`
 * still only ever receives DOMPurify output (DOMParser documents are inert).
 */
export function markBenefitHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  for (const row of Array.from(doc.querySelectorAll('tr'))) {
    const text = (row.textContent ?? '').replace(/\u00a0/g, ' ').trim()
    if (!text) row.classList.add('vr-blank-row')
  }
  for (const li of Array.from(doc.querySelectorAll('li'))) {
    const first = li.firstElementChild
    if (first && (first.textContent ?? '').replace(/\s+/g, '') === '•') {
      first.classList.add('vr-bullet')
    }
  }
  return doc.body.innerHTML
}
